import { ButtonGradientPrimary, CareerTargetVisual } from '@amelio/ui-library';

import {
    GROWTH_CAPTION,
    GROWTH_GLASS_FILL,
    GROWTH_PLACEHOLDER,
    GROWTH_SECTION_PADDING,
    GROWTH_SECTION_TITLE,
} from './growthStyles';

import type { CareerAspiration, TargetPosition } from '@/types/career-growth';

import { useAmelioTranslations } from '@/hooks';
import { IMPORTANCE_LEVEL_MAX } from '@/types/career-growth';

/**
 * The height the design gives this row, in real pixels, as a floor rather than a fixed height. The
 * card takes it rather than stretching to the career path beside it: stretched, the surplus all fell
 * into the target's box, which centres its picture and so opened a hole above and below it. A floor
 * keeps that fix while letting the card grow when its target comes with how much it matters to the
 * employee, which needs more room than a fixed height leaves.
 */
const CARD_HEIGHT = 'lg:min-h-[456px]';

const FACT_TITLE = 'text-navy text-lg leading-tight font-semibold';

interface CareerObjectiveCardProps {
    /** Where the employee is heading, as the API reconciles it; `null` when nothing names one. */
    targetPosition: TargetPosition | null;
    aspiration: CareerAspiration | null;
    employeeFirstName: string;
    onReEvaluate: () => void;
    dataTestId?: string;
}

/**
 * The "Objectif de carrière" card and the button that goes and plans a new "Aspirations de carrière"
 * meeting.
 *
 * One target is shown, not two. The manager decides it, and two places record that decision: the
 * `Target` step of the career path, and the "Aspirations de carrière" meeting. The API keeps the more
 * recently written of the two, so a new meeting overwrites what is shown here and so does an edit --
 * whichever came last wins. This card states that winner, never both records side by side.
 *
 * How much the position matters to the employee is recorded with the aspiration, so it is shown only
 * when the aspiration is what won: read beside a target that came from the career path, it would
 * belong to a different position than the one above it.
 */
export const CareerObjectiveCard = ({
    targetPosition,
    aspiration,
    employeeFirstName,
    onReEvaluate,
    dataTestId = 'career-objective-card',
}: CareerObjectiveCardProps) => {
    const { t } = useAmelioTranslations();

    const targetTitle = targetPosition?.title?.trim() || null;
    const importanceLevel =
        targetPosition?.source === 'Aspiration' ? (aspiration?.importanceLevel ?? null) : null;
    // With a position named, the path only repeats it; without one, it is what the latest
    // aspiration has to say, and saying "none recorded" over it would be false.
    const standInPath = targetTitle ? null : (aspiration?.developmentPath ?? null);
    const hasObjective = Boolean(targetTitle || standInPath);

    return (
        <section
            className={`${GROWTH_GLASS_FILL} ${GROWTH_SECTION_PADDING} ${CARD_HEIGHT} flex flex-col items-center gap-4 rounded-[24px] text-center`}
            data-testid={dataTestId}
        >
            <h3 className={GROWTH_SECTION_TITLE}>
                {t('performanceProfile.growth.careerObjective.title')}
            </h3>

            {/* The library caps the visual at `max-w-52`: the cap is lifted from the wrapper, whose
                child selector outweighs a single class. */}
            <div className={'flex w-full flex-1 items-center justify-center [&>svg]:max-w-[210px]'}>
                <CareerTargetVisual isTargetSet={Boolean(targetTitle)} />
            </div>

            {hasObjective ? (
                <div className={'flex flex-col items-center gap-2'}>
                    {targetTitle && (
                        <div
                            className={'flex flex-col items-center gap-1'}
                            data-testid={`${dataTestId}-target-step`}
                        >
                            <span className={GROWTH_CAPTION}>
                                {t('performanceProfile.growth.careerObjective.targetStep')}
                            </span>
                            <span className={FACT_TITLE}>{targetTitle}</span>
                            {importanceLevel !== null && (
                                <span className={GROWTH_CAPTION}>
                                    {t('performanceProfile.growth.careerObjective.importanceFor', {
                                        name: employeeFirstName,
                                        level: importanceLevel,
                                        max: IMPORTANCE_LEVEL_MAX,
                                    })}
                                </span>
                            )}
                        </div>
                    )}

                    {standInPath && (
                        <div
                            className={'flex flex-col items-center gap-1'}
                            data-testid={`${dataTestId}-development-path`}
                        >
                            <span className={GROWTH_CAPTION}>
                                {t('performanceProfile.growth.careerObjective.developmentPath')}
                            </span>
                            <span className={FACT_TITLE}>
                                {t(`performanceProfile.growth.aspiration.paths.${standInPath}`)}
                            </span>
                        </div>
                    )}
                </div>
            ) : (
                <div
                    className={'flex flex-col items-center gap-1'}
                    data-testid={`${dataTestId}-empty`}
                >
                    <span className={'text-navy text-base leading-tight font-semibold'}>
                        {t(
                            aspiration
                                ? 'performanceProfile.growth.careerObjective.recordedWithoutTarget'
                                : 'performanceProfile.growth.careerObjective.empty',
                        )}
                    </span>
                    <span className={GROWTH_PLACEHOLDER}>
                        {t('performanceProfile.growth.careerObjective.emptyHint')}
                    </span>
                </div>
            )}

            {/* The label is longer than this narrow column: `wrap` gives it a second line with the
                padding and the minimum height that go with it, rather than a fixed height its text
                fills edge to edge. */}
            <ButtonGradientPrimary
                type={'button'}
                onClick={onReEvaluate}
                size={'md'}
                wrap
                className={'w-full max-w-60'}
                dataTestId={`${dataTestId}-re-evaluate`}
            >
                {t('performanceProfile.growth.careerObjective.reEvaluate')}
            </ButtonGradientPrimary>
        </section>
    );
};
